<template>
  <div class="customise-step">
    <v-expansion-panels
      :model-value="openPanel"
      variant="accordion"
      mandatory
      flat
      class="customise-panels"
      @update:model-value="onPanelChange"
    >
      <!-- Your brand colours and rewards card stamps -->
      <v-expansion-panel value="branding" class="customise-panel">
        <v-expansion-panel-title class="customise-panel-title">
          Your brand colours and rewards card stamps
        </v-expansion-panel-title>
        <v-expansion-panel-text>
          <StepPreview
            v-if="inlinePreview && preview"
            :preview="preview"
            class="inline-preview"
          />
          <BrandingEditor
            :model-value="branding"
            hide-display-name
            hide-logo
            :show-aux-validation="showAuxValidation"
            @update:model-value="$emit('update:branding', $event)"
          />
        </v-expansion-panel-text>
      </v-expansion-panel>

      <!-- Customer sign-up page -->
      <v-expansion-panel value="web_page" class="customise-panel">
        <v-expansion-panel-title class="customise-panel-title">
          Customer sign-up page wording
        </v-expansion-panel-title>
        <v-expansion-panel-text>
          <StepPreview
            v-if="inlinePreview && preview"
            :preview="preview"
            class="inline-preview"
          />
          <v-text-field
            :model-value="title"
            label="Headline"
            variant="outlined"
            density="comfortable"
            class="mb-3"
            @update:model-value="$emit('update:title', $event)"
          >
            <template #append-inner>
              <v-tooltip location="top" max-width="280">
                <template #activator="{ props }">
                  <v-icon
                    v-bind="props"
                    icon="mdi-help-circle-outline"
                    size="small"
                    class="field-help-icon"
                    aria-label="Headline help"
                  />
                </template>
                <span>{{ hints.title }}</span>
              </v-tooltip>
            </template>
          </v-text-field>

          <v-text-field
            :model-value="subtitle"
            label="Sub-heading"
            variant="outlined"
            density="comfortable"
            class="mb-3"
            @update:model-value="$emit('update:subtitle', $event)"
          >
            <template #append-inner>
              <v-tooltip location="top" max-width="280">
                <template #activator="{ props }">
                  <v-icon
                    v-bind="props"
                    icon="mdi-help-circle-outline"
                    size="small"
                    class="field-help-icon"
                    aria-label="Sub-heading help"
                  />
                </template>
                <span>{{ hints.subtitle }}</span>
              </v-tooltip>
            </template>
          </v-text-field>

          <div class="reuse-note">
            <v-icon class="reuse-note-icon">mdi-information-outline</v-icon>
            Your sign-up page uses the same logo and colours as your card.
          </div>
        </v-expansion-panel-text>
      </v-expansion-panel>

      <!-- QR code counter sign -->
      <v-expansion-panel value="qr_design" class="customise-panel">
        <v-expansion-panel-title class="customise-panel-title">
          QR code counter sign
        </v-expansion-panel-title>
        <v-expansion-panel-text>
          <StepPreview
            v-if="inlinePreview && preview"
            :preview="preview"
            class="inline-preview"
          />
          <div class="qr-design-controls">
            <v-textarea
              :model-value="qrHeadline"
              label="Headline"
              variant="outlined"
              density="comfortable"
              rows="2"
              auto-grow
              maxlength="90"
              counter="90"
              @update:model-value="$emit('update:qrHeadline', $event)"
            >
              <template #append-inner>
                <v-tooltip location="top" max-width="280">
                  <template #activator="{ props }">
                    <v-icon
                      v-bind="props"
                      icon="mdi-help-circle-outline"
                      size="small"
                      class="field-help-icon"
                      aria-label="Counter sign headline help"
                    />
                  </template>
                  <span>{{ hints.qrHeadline }}</span>
                </v-tooltip>
              </template>
            </v-textarea>

            <div class="qr-toggle-row">
              <v-switch
                :model-value="qrShowLogo"
                color="primary"
                density="compact"
                hide-details
                :disabled="!hasLogo"
                :label="hasLogo ? 'Show my logo on the counter sign' : 'Upload a logo to show it on the counter sign'"
                class="qr-toggle"
                @update:model-value="$emit('update:qrShowLogo', $event)"
              />
            </div>
          </div>

          <div class="reuse-note">
            <v-icon class="reuse-note-icon">mdi-information-outline</v-icon>
            <span>
              This headline goes on the counter sign. It can't be changed from your dashboard afterwards, so check the wording.
              Colours come from your branding, and the QR code redirects to lulurewards.co.uk/business/{{ businessId }}.
            </span>
          </div>
        </v-expansion-panel-text>
      </v-expansion-panel>
    </v-expansion-panels>
  </div>
</template>

<script>
import { computed } from "vue";
import BrandingEditor from "@/components/Organisms/BrandingEditor.vue";
import StepPreview from "@/components/Molecules/StepPreview.vue";

// The optional "Make it yours" step of business sign-up: card colours and
// stamps, sign-up page wording and the QR code counter sign, one accordion panel
// each. Everything here has a ready-made default, so the step is always
// valid and can be skipped. The parent owns the form state and the preview;
// this component only renders the fields and reports which panel is open.
export default {
  name: "CustomisationStep",
  components: { BrandingEditor, StepPreview },
  emits: [
    "update:openPanel",
    "update:branding",
    "update:title",
    "update:subtitle",
    "update:qrHeadline",
    "update:qrShowLogo",
  ],
  props: {
    // "branding" | "web_page" | "qr_design"
    openPanel: {
      type: String,
      default: "branding",
    },
    // BrandingEditor slice: colours, threshold and aux fields.
    branding: {
      type: Object,
      required: true,
    },
    // True once the owner has tried to continue, so the "fill in both or
    // neither" message for the optional extras only appears after that.
    showAuxValidation: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      default: "",
    },
    subtitle: {
      type: String,
      default: "",
    },
    qrHeadline: {
      type: String,
      default: "",
    },
    qrShowLogo: {
      type: Boolean,
      default: true,
    },
    // Whether the owner has chosen a logo (drives the counter sign toggle).
    hasLogo: {
      type: Boolean,
      default: false,
    },
    businessId: {
      type: String,
      default: "",
    },
    // { component, props, caption, disclaimer } for the open panel.
    preview: {
      type: Object,
      default: null,
    },
    // On narrow screens the parent has no room for the sticky aside, so
    // the preview is rendered inside the open panel instead.
    inlinePreview: {
      type: Boolean,
      default: false,
    },
  },
  setup(props, { emit }) {
    // The headline example uses the owner's own business name once typed.
    const hints = computed(() => {
      const name = (props.branding?.displayName || "").trim() || "your business";
      return {
        title: `The headline customers see when they join, e.g. 'Welcome to the ${name} community!'. Keep it short and warm.`,
        subtitle:
          "One sentence explaining the reward, e.g. 'Join our rewards programme and get a treat on every 9th visit.' This sets expectations.",
        qrHeadline:
          'The message printed above your QR code, e.g. "Join our rewards programme - every 9th visit you get a treat!".',
      };
    });

    // `mandatory` keeps one panel open, so the value is always a panel key;
    // only report genuine changes so the parent can track user expansions.
    const onPanelChange = (value) => {
      if (!value || value === props.openPanel) return;
      emit("update:openPanel", value);
    };

    return {
      hints,
      onPanelChange,
    };
  },
};
</script>

<style scoped>
.customise-step {
  display: flex;
  flex-direction: column;
}

.customise-panels {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.customise-panel {
  border: 1px solid var(--lulu-border);
  border-radius: 12px !important;
  background: var(--lulu-surface);
  overflow: hidden;
}

.customise-panel::after {
  display: none;
}

.customise-panel-title {
  font-size: 14px;
  font-weight: 700;
  color: var(--lulu-text-strong);
  /* Vuetify grows an open panel's title to 64px; keep it steady. */
  min-height: 52px !important;
  padding: 0 18px;
}

.customise-panel :deep(.v-expansion-panel-text__wrapper) {
  padding: 8px 18px 18px;
}

@media (max-width: 600px) {
  .customise-panel-title {
    padding: 0 14px;
  }
  .customise-panel :deep(.v-expansion-panel-text__wrapper) {
    padding: 8px 12px 16px;
  }
}

/* Mobile-only preview shown above the open panel's fields. StepPreview
   scales the fixed-width mockups down to fit. */
.inline-preview {
  margin: 4px auto 18px;
}

.field-help-icon {
  color: var(--lulu-text-muted);
  opacity: 0.7;
  cursor: help;
  transition: color 0.15s, opacity 0.15s;
}

.field-help-icon:hover {
  opacity: 1;
  color: var(--lulu-primary);
}

.reuse-note {
  display: flex;
  align-items: flex-start;
  line-height: 1.5;
  gap: 8px;
  padding: 12px 14px;
  background: rgba(0, 109, 62, 0.06);
  border: 1px solid rgba(0, 109, 62, 0.15);
  border-radius: 10px;
  font-size: 13px;
  color: rgb(0, 85, 48);
}

.reuse-note-icon {
  color: var(--lulu-primary);
  font-size: 18px !important;
}

/* QR code counter sign panel */
.qr-design-controls {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 18px;
}

.qr-toggle-row {
  display: flex;
}

.qr-toggle :deep(.v-label) {
  font-size: 13px;
  opacity: 1;
}
</style>
